import { useState, useEffect } from 'react';
import { FundraisingTermSheet, calculateTermSheetMetrics } from '../../types';
import { Button, Input, Select, Textarea } from "@repo/o37-ui";
import { X } from "lucide-react";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface TermSheetFormProps {
  fundId: string;
  termSheet?: FundraisingTermSheet | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function TermSheetForm({ fundId, termSheet, onClose, onSaved }: TermSheetFormProps) {
  const isEditing = !!termSheet;

  const [settings, setSettings] = useState<any>(null);
  const [investmentAmount, setInvestmentAmount] = useState(termSheet?.investmentAmount?.toString() || '');
  const [preMoneyValuation, setPreMoneyValuation] = useState(termSheet?.preMoneyValuation?.toString() || '');
  const [liquidationPreference, setLiquidationPreference] = useState(termSheet?.liquidationPreference?.toString() || '1');
  const [participationCap, setParticipationCap] = useState(termSheet?.participationCap?.toString() || '');
  const [boardSeats, setBoardSeats] = useState(termSheet?.boardSeats?.toString() || '0');
  const [proRataRights, setProRataRights] = useState(termSheet?.proRataRights !== false);
  const [antiDilution, setAntiDilution] = useState(termSheet?.antiDilution || 'broad_based');
  const [status, setStatus] = useState(termSheet?.status || 'received');
  const [expiresAt, setExpiresAt] = useState(termSheet?.expiresAt?.split('T')[0] || '');
  const [documentUrl, setDocumentUrl] = useState(termSheet?.documentUrl || '');
  const [notes, setNotes] = useState(termSheet?.notes || '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  // Load settings on mount to use as defaults
  useEffect(() => {
    if (!isEditing) {
      fetch(`${API_URL}/settings/term-sheets`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      })
        .then(res => res.json())
        .then(data => {
          const s = data.settings;
          setSettings(s);
          // Apply defaults only if not editing
          setLiquidationPreference(s.defaultLiquidationPreference?.toString() || '1');
          setProRataRights(s.defaultProRataRights !== false);
          setAntiDilution(s.defaultAntiDilution || 'broad_based');
          setBoardSeats(s.defaultBoardSeats?.toString() || '0');
          
          // Set expiration date based on settings
          if (s.expirationDays) {
            const expirationDate = new Date();
            expirationDate.setDate(expirationDate.getDate() + s.expirationDays);
            setExpiresAt(expirationDate.toISOString().split('T')[0]);
          }
        })
        .catch(err => console.error('Failed to load settings:', err));
    }
  }, [isEditing, userId, userRole]);

  // Calculate metrics for preview
  const investment = parseFloat(investmentAmount) || 0;
  const preMoney = parseFloat(preMoneyValuation) || 0;
  const metrics = investment > 0 && preMoney > 0 
    ? calculateTermSheetMetrics(investment, preMoney)
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!investmentAmount) {
      setError('Investment amount is required');
      return;
    }

    // Validate based on settings
    if (settings?.requirePreMoneyValuation && !preMoneyValuation) {
      setError('Pre-money valuation is required by settings');
      return;
    }
    if (settings?.requireDocumentUrl && !documentUrl) {
      setError('Document URL is required by settings');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        investmentAmount: parseFloat(investmentAmount),
        preMoneyValuation: preMoneyValuation ? parseFloat(preMoneyValuation) : null,
        liquidationPreference: parseFloat(liquidationPreference) || 1,
        participationCap: participationCap ? parseFloat(participationCap) : null,
        boardSeats: parseInt(boardSeats) || 0,
        proRataRights,
        antiDilution,
        status,
        expiresAt: expiresAt || null,
        documentUrl: documentUrl || null,
        notes: notes || null
      };

      const url = isEditing
        ? `${API_URL}/fundraising/funds/${fundId}/term-sheets/${termSheet.id}`
        : `${API_URL}/fundraising/funds/${fundId}/term-sheets`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSaved();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save term sheet');
      }
    } catch (e) {
      setError('Failed to save term sheet');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-100">
          <h2 className="text-xl font-semibold">
            {isEditing ? 'Edit Term Sheet' : 'Add Term Sheet'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {/* Calculator Preview */}
          {metrics && (
            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
              <h3 className="text-sm font-medium text-emerald-800 mb-3">Calculated Metrics</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-xs text-emerald-600">Post-Money Valuation</div>
                  <div className="text-lg font-semibold text-emerald-900">
                    {formatCurrency(metrics.postMoneyValuation)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-emerald-600">Dilution %</div>
                  <div className="text-lg font-semibold text-emerald-900">
                    {metrics.dilutionPercentage.toFixed(2)}%
                  </div>
                </div>
                <div>
                  <div className="text-xs text-emerald-600">Ownership %</div>
                  <div className="text-lg font-semibold text-emerald-900">
                    {metrics.ownershipPercentage.toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Core Terms */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Investment Amount *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                <Input
                  value={investmentAmount}
                  onChange={(e) => setInvestmentAmount(e.target.value)}
                  placeholder="10000000"
                  type="number"
                  required
                  className="pl-7"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Valuation {settings?.requirePreMoneyValuation && <span className="text-red-500">*</span>}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                <Input
                  value={preMoneyValuation}
                  onChange={(e) => setPreMoneyValuation(e.target.value)}
                  placeholder="40000000"
                  type="number"
                  className="pl-7"
                  required={settings?.requirePreMoneyValuation}
                />
              </div>
            </div>
          </div>

          {/* Additional Terms */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Liquidation Preference
              </label>
              <div className="relative">
                <Input
                  value={liquidationPreference}
                  onChange={(e) => setLiquidationPreference(e.target.value)}
                  placeholder="1"
                  type="number"
                  step="0.1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">x</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Participation Cap
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                <Input
                  value={participationCap}
                  onChange={(e) => setParticipationCap(e.target.value)}
                  placeholder="Optional"
                  type="number"
                  className="pl-7"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Board Seats
              </label>
              <Input
                value={boardSeats}
                onChange={(e) => setBoardSeats(e.target.value)}
                placeholder="0"
                type="number"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Anti-Dilution
              </label>
              <Select value={antiDilution} onChange={(e) => setAntiDilution(e.target.value as any)}>
                <option value="broad_based">Broad-Based Weighted Average</option>
                <option value="narrow_based">Narrow-Based Weighted Average</option>
                <option value="full_ratchet">Full Ratchet</option>
                <option value="none">None</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Status
              </label>
              <Select value={status} onChange={(e) => setStatus(e.target.value as any)}>
                <option value="received">Received</option>
                <option value="reviewing">Reviewing</option>
                <option value="negotiating">Negotiating</option>
                <option value="accepted">Accepted</option>
                <option value="declined">Declined</option>
                <option value="expired">Expired</option>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={proRataRights}
                onChange={(e) => setProRataRights(e.target.checked)}
                className="rounded border-neutral-300"
              />
              Pro-Rata Rights
            </label>
            
            <div className="flex-1">
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Expires
              </label>
              <Input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Document URL {settings?.requireDocumentUrl && <span className="text-red-500">*</span>}
            </label>
            <Input
              value={documentUrl}
              onChange={(e) => setDocumentUrl(e.target.value)}
              placeholder="https://..."
              type="url"
              required={settings?.requireDocumentUrl}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notes
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about this term sheet..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} text="Cancel" />
            <Button type="submit" disabled={saving} text={saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Term Sheet'} />
          </div>
        </form>
      </div>
    </div>
  );
}
